import React, { useState, useEffect } from 'react';
import { X, ArrowUpRight } from 'lucide-react';
import Logo from '../common/Logo';
import { getCategories } from '../../data/storeData';

const MobileMenu = ({ isOpen, onClose, setActiveCategory }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await getCategories();
        setCategories(cats || []);
      } catch (error) {
        console.error('Error loading categories:', error);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();

    const handleUpdate = () => loadCategories();
    window.addEventListener('categoriesUpdated', handleUpdate);

    return () => {
      window.removeEventListener('categoriesUpdated', handleUpdate);
    };
  }, []);

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleNavClick = (category) => {
    setActiveCategory(category);
    onClose();
    const catalog = document.getElementById('catalog');
    if (catalog) catalog.scrollIntoView({ behavior: 'smooth' });
  };

  const menuItems = [
    { id: 'all', name: 'Store' },
    ...(!loading ? categories : [])
  ];

  return (
    <div
      className={`fixed inset-0 z-50 transition-all duration-500 ${
        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/95 backdrop-blur-3xl" onClick={onClose} />

      {/* Ambient glow */}
      <div className="absolute top-1/4 -right-32 w-[400px] h-[400px] bg-orange-500/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 -left-32 w-[350px] h-[350px] bg-blue-600/10 rounded-full blur-[100px] pointer-events-none" />

      <div className="relative flex flex-col h-full p-6 safe-area-inset">
        {/* Header */}
        <div className="flex justify-between items-center mb-14">
          <Logo className="h-8 text-white" />
          <button
            onClick={onClose}
            className="p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all duration-300 active:scale-90"
            aria-label="Close menu"
          >
            <X className="w-6 h-6" strokeWidth={1.5} />
          </button>
        </div>

        {/* Nav Items — staggered entrance */}
        <nav className="flex-1 overflow-y-auto no-scrollbar flex flex-col">
          <div className="my-auto flex flex-col gap-2 py-4">
          {menuItems.map((item, i) => (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.id)}
              className="group flex items-center justify-between py-4 border-b border-white/10 text-left transition-all duration-500"
              style={{
                opacity: isOpen ? 1 : 0,
                transform: isOpen ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity 0.5s ease ${100 + i * 70}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${100 + i * 70}ms`
              }}
            >
              <span className="text-4xl font-bold tracking-tight text-white group-hover:text-orange-500 group-active:text-orange-500 transition-colors duration-300">
                {item.name}
              </span>
              <ArrowUpRight className="w-7 h-7 text-white/20 group-hover:text-orange-500 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" strokeWidth={1.5} />
            </button>
          ))}
          </div>
        </nav>

        {/* Footer */}
        <div
          className="pt-6 text-center"
          style={{
            opacity: isOpen ? 1 : 0,
            transition: `opacity 0.6s ease ${200 + menuItems.length * 70}ms`
          }}
        >
          <p className="text-[11px] tracking-[0.25em] uppercase text-white/30 font-medium">
            Premium Retail &amp; Wholesale
          </p>
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;
